import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../components/Layout/Header';
import api, { employeeMasterAPI, departmentAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { 
  ArrowLeft, Save, User, Briefcase, MapPin, CreditCard, 
  Users, GraduationCap, Phone, Shield, Building2, Loader2
} from 'lucide-react';

const EmployeeMasterForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [activeTab, setActiveTab] = useState('basic');
  
  // Form state
  const [form, setForm] = useState({
    // Joining & Appointment
    employee_code: '',
    date_of_appointment: '',
    date_of_joining: '',
    appointment_type: 'direct_recruit',
    initial_designation: '',
    initial_grade: '',
    file_number: '',
    
    // Employment
    employment_type: 'permanent',
    employee_group: '',
    cadre: '',
    
    // Probation
    is_on_probation: false,
    probation_period_months: '',
    probation_start_date: '',
    probation_end_date: '',
    
    // Personal
    first_name: '',
    middle_name: '',
    last_name: '',
    email: '',
    personal_email: '',
    date_of_birth: '',
    gender: 'male',
    father_name: '',
    mother_name: '',
    spouse_name: '',
    marital_status: 'single',
    marriage_date: '',
    nationality: 'Indian',
    religion: '',
    caste: '',
    is_special_category: false,
    special_category_type: '',
    blood_group: '',
    
    // Contact
    mobile_primary: '',
    mobile_secondary: '',
    phone_residence: '',
    phone_office: '',
    
    // Identification
    aadhaar_number: '',
    pan_number: '',
    passport_number: '',
    passport_expiry_date: '',
    driving_license_number: '',
    voter_id: '',
    
    // Current Posting
    posting_office_id: '',
    department_id: '',
    designation: '',
    grade: '',
    
    // Reporting
    reporting_manager_id: '',
    
    // Pay Details
    pay_level: '',
    pay_scale: '',
    basic_pay: 0,
    gpf_number: '',
    epf_number: '',
    uan_number: '',
    pran_number: '',
    esi_number: '',
    
    // Accommodation
    accommodation_type: 'none',
    
    // Status
    status: 'active',
  });

  useEffect(() => {
    fetchDepartments();
    if (isEditMode) {
      fetchEmployee();
    }
  }, [id]);

  const fetchDepartments = async () => {
    try {
      const res = await departmentAPI.getAll();
      setDepartments(res.data);
    } catch (error) {
      console.error('Failed to fetch departments');
    }
  };

  const fetchEmployee = async () => {
    setLoading(true);
    try {
      const res = await employeeMasterAPI.getById(id);
      setForm(prev => ({ ...prev, ...res.data }));
    } catch (error) {
      toast.error('Failed to fetch employee');
      navigate('/employee-master');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!form.employee_code || !form.first_name || !form.last_name || !form.email) {
      toast.error('Please fill all required fields');
      setActiveTab('basic');
      return;
    }
    
    if (!form.date_of_joining || !form.department_id || !form.designation) {
      toast.error('Please fill appointment details');
      setActiveTab('employment');
      return;
    }
    
    if (!form.mobile_primary) {
      toast.error('Primary mobile is required');
      setActiveTab('contact');
      return;
    }
    
    if (!form.posting_office_id) {
      // Auto-fill posting_office_id with department_id if not set
      form.posting_office_id = form.department_id;
    }
    
    // Set initial values from current posting for new employees
    if (!isEditMode) {
      form.initial_designation = form.designation;
      form.initial_grade = form.grade;
    }
    
    setSaving(true);
    try {
      if (isEditMode) {
        await employeeMasterAPI.update(id, form);
        toast.success('Employee updated successfully. Pending authorization.');
      } else {
        await employeeMasterAPI.create(form);
        toast.success('Employee created successfully. Pending authorization.');
      }
      navigate('/employee-master');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to save employee';
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div data-testid="employee-master-form">
      <Header 
        title={
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/employee-master')}>
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <span>{isEditMode ? 'Edit Employee' : 'Add New Employee'}</span>
          </div>
        }
        subtitle={isEditMode ? `Editing: ${form.employee_code}` : 'Central Employee Master - 360° Profile'}
        action={
          <Button onClick={handleSubmit} disabled={saving} data-testid="save-employee-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            {saving ? 'Saving...' : 'Save Employee'}
          </Button>
        }
      />

      <form onSubmit={handleSubmit} className="p-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white border rounded-lg p-1 flex-wrap h-auto">
            <TabsTrigger value="basic" className="gap-2">
              <User className="w-4 h-4" /> Basic Info
            </TabsTrigger>
            <TabsTrigger value="employment" className="gap-2">
              <Briefcase className="w-4 h-4" /> Employment
            </TabsTrigger>
            <TabsTrigger value="contact" className="gap-2">
              <Phone className="w-4 h-4" /> Contact
            </TabsTrigger>
            <TabsTrigger value="identity" className="gap-2">
              <Shield className="w-4 h-4" /> Identity
            </TabsTrigger>
            <TabsTrigger value="statutory" className="gap-2">
              <CreditCard className="w-4 h-4" /> Statutory
            </TabsTrigger>
          </TabsList>

          {/* Basic Information Tab */}
          <TabsContent value="basic">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <User className="w-5 h-5 text-blue-600" />
                    Personal Details
                  </CardTitle>
                  <CardDescription>Basic personal information</CardDescription>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-4">
                  <div className="col-span-2 sm:col-span-1">
                    <Label htmlFor="first_name">First Name <span className="text-red-500">*</span></Label>
                    <Input
                      id="first_name"
                      value={form.first_name}
                      onChange={(e) => handleChange('first_name', e.target.value)}
                      placeholder="Enter first name"
                      required
                      data-testid="input-first-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="middle_name">Middle Name</Label>
                    <Input
                      id="middle_name"
                      value={form.middle_name}
                      onChange={(e) => handleChange('middle_name', e.target.value)}
                      placeholder="Enter middle name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="last_name">Last Name <span className="text-red-500">*</span></Label>
                    <Input
                      id="last_name"
                      value={form.last_name}
                      onChange={(e) => handleChange('last_name', e.target.value)}
                      placeholder="Enter last name"
                      required
                      data-testid="input-last-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="date_of_birth">Date of Birth <span className="text-red-500">*</span></Label>
                    <Input
                      id="date_of_birth"
                      type="date"
                      value={form.date_of_birth}
                      onChange={(e) => handleChange('date_of_birth', e.target.value)}
                      required
                      data-testid="input-dob"
                    />
                  </div>
                  <div>
                    <Label htmlFor="gender">Gender <span className="text-red-500">*</span></Label>
                    <Select value={form.gender} onValueChange={(v) => handleChange('gender', v)}>
                      <SelectTrigger data-testid="select-gender">
                        <SelectValue placeholder="Select gender" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="male">Male</SelectItem>
                        <SelectItem value="female">Female</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="blood_group">Blood Group</Label>
                    <Select value={form.blood_group || ''} onValueChange={(v) => handleChange('blood_group', v)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select blood group" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="A+">A+</SelectItem>
                        <SelectItem value="A-">A-</SelectItem>
                        <SelectItem value="B+">B+</SelectItem>
                        <SelectItem value="B-">B-</SelectItem>
                        <SelectItem value="AB+">AB+</SelectItem>
                        <SelectItem value="AB-">AB-</SelectItem>
                        <SelectItem value="O+">O+</SelectItem>
                        <SelectItem value="O-">O-</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="marital_status">Marital Status</Label>
                    <Select value={form.marital_status} onValueChange={(v) => handleChange('marital_status', v)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="single">Single</SelectItem>
                        <SelectItem value="married">Married</SelectItem>
                        <SelectItem value="divorced">Divorced</SelectItem>
                        <SelectItem value="widowed">Widowed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="nationality">Nationality</Label>
                    <Input
                      id="nationality"
                      value={form.nationality}
                      onChange={(e) => handleChange('nationality', e.target.value)}
                      placeholder="Enter nationality"
                    />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-blue-600" />
                    Family Details
                  </CardTitle>
                  <CardDescription>Parent and spouse information</CardDescription>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label htmlFor="father_name">Father's Name</Label>
                    <Input
                      id="father_name"
                      value={form.father_name}
                      onChange={(e) => handleChange('father_name', e.target.value)}
                      placeholder="Enter father's name"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label htmlFor="mother_name">Mother's Name</Label>
                    <Input
                      id="mother_name"
                      value={form.mother_name}
                      onChange={(e) => handleChange('mother_name', e.target.value)}
                      placeholder="Enter mother's name"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label htmlFor="spouse_name">Spouse Name</Label>
                    <Input
                      id="spouse_name"
                      value={form.spouse_name}
                      onChange={(e) => handleChange('spouse_name', e.target.value)}
                      placeholder="Enter spouse name (if married)"
                      disabled={form.marital_status === 'single'}
                    />
                  </div>
                  {form.marital_status === 'married' && (
                    <div className="col-span-2">
                      <Label htmlFor="marriage_date">Marriage Date</Label>
                      <Input
                        id="marriage_date"
                        type="date"
                        value={form.marriage_date}
                        onChange={(e) => handleChange('marriage_date', e.target.value)}
                      />
                    </div>
                  )}
                  <div>
                    <Label htmlFor="religion">Religion</Label>
                    <Input
                      id="religion"
                      value={form.religion}
                      onChange={(e) => handleChange('religion', e.target.value)}
                      placeholder="Religion"
                    />
                  </div>
                  <div>
                    <Label htmlFor="caste">Caste</Label>
                    <Input
                      id="caste"
                      value={form.caste}
                      onChange={(e) => handleChange('caste', e.target.value)}
                      placeholder="Caste"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label htmlFor="special_category_type">Special Category (SC/ST/OBC)</Label>
                    <Select value={form.special_category_type || ''} onValueChange={(v) => handleChange('special_category_type', v)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="general">General</SelectItem>
                        <SelectItem value="sc">SC</SelectItem>
                        <SelectItem value="st">ST</SelectItem>
                        <SelectItem value="obc">OBC</SelectItem>
                        <SelectItem value="ews">EWS</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Employment Tab */}
          <TabsContent value="employment">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Briefcase className="w-5 h-5 text-blue-600" />
                    Appointment Details
                  </CardTitle>
                  <CardDescription>Joining and appointment information</CardDescription>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="employee_code">Employee Code <span className="text-red-500">*</span></Label>
                    <Input
                      id="employee_code"
                      value={form.employee_code}
                      onChange={(e) => handleChange('employee_code', e.target.value.toUpperCase())}
                      placeholder="e.g., EMP001"
                      required
                      disabled={isEditMode}
                      data-testid="input-emp-code"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Official Email <span className="text-red-500">*</span></Label>
                    <Input
                      id="email"
                      type="email"
                      value={form.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      placeholder="employee@company.com"
                      required
                      data-testid="input-email"
                    />
                  </div>
                  <div>
                    <Label htmlFor="date_of_appointment">Date of Appointment</Label>
                    <Input
                      id="date_of_appointment"
                      type="date"
                      value={form.date_of_appointment}
                      onChange={(e) => handleChange('date_of_appointment', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="date_of_joining">Date of Joining <span className="text-red-500">*</span></Label>
                    <Input
                      id="date_of_joining"
                      type="date"
                      value={form.date_of_joining}
                      onChange={(e) => handleChange('date_of_joining', e.target.value)}
                      required
                      data-testid="input-doj"
                    />
                  </div>
                  <div>
                    <Label htmlFor="appointment_type">Appointment Type</Label>
                    <Select value={form.appointment_type} onValueChange={(v) => handleChange('appointment_type', v)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="direct_recruit">Direct Recruit</SelectItem>
                        <SelectItem value="promotion">Promotion</SelectItem>
                        <SelectItem value="transfer">Transfer</SelectItem>
                        <SelectItem value="deputation">Deputation</SelectItem>
                        <SelectItem value="absorption">Absorption</SelectItem>
                        <SelectItem value="compassionate">Compassionate</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="employment_type">Employment Type</Label>
                    <Select value={form.employment_type} onValueChange={(v) => handleChange('employment_type', v)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="permanent">Permanent</SelectItem>
                        <SelectItem value="contractual">Contractual</SelectItem>
                        <SelectItem value="trainee">Trainee</SelectItem>
                        <SelectItem value="probationer">Probationer</SelectItem>
                        <SelectItem value="consultant">Consultant</SelectItem>
                        <SelectItem value="temporary">Temporary</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="file_number">File Number</Label>
                    <Input
                      id="file_number"
                      value={form.file_number}
                      onChange={(e) => handleChange('file_number', e.target.value)}
                      placeholder="File/Reference number"
                    />
                  </div>
                  <div>
                    <Label htmlFor="cadre">Cadre</Label>
                    <Input
                      id="cadre"
                      value={form.cadre}
                      onChange={(e) => handleChange('cadre', e.target.value)}
                      placeholder="e.g., Officer, Staff"
                    />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-blue-600" />
                    Current Posting
                  </CardTitle>
                  <CardDescription>Department and designation details</CardDescription>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label htmlFor="department_id">Department <span className="text-red-500">*</span></Label>
                    <Select value={form.department_id} onValueChange={(v) => handleChange('department_id', v)}>
                      <SelectTrigger data-testid="select-department">
                        <SelectValue placeholder="Select department" />
                      </SelectTrigger>
                      <SelectContent>
                        {departments.map(d => (
                          <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="designation">Designation <span className="text-red-500">*</span></Label>
                    <Input
                      id="designation"
                      value={form.designation}
                      onChange={(e) => handleChange('designation', e.target.value)}
                      placeholder="e.g., Manager"
                      required
                      data-testid="input-designation"
                    />
                  </div>
                  <div>
                    <Label htmlFor="grade">Grade</Label>
                    <Input
                      id="grade"
                      value={form.grade}
                      onChange={(e) => handleChange('grade', e.target.value)}
                      placeholder="e.g., A, B, C"
                    />
                  </div>
                  <div>
                    <Label htmlFor="pay_level">Pay Level</Label>
                    <Input
                      id="pay_level"
                      value={form.pay_level}
                      onChange={(e) => handleChange('pay_level', e.target.value)}
                      placeholder="e.g., Level 10"
                    />
                  </div>
                  <div>
                    <Label htmlFor="pay_scale">Pay Scale</Label>
                    <Input
                      id="pay_scale"
                      value={form.pay_scale}
                      onChange={(e) => handleChange('pay_scale', e.target.value)}
                      placeholder="e.g., 56100-177500"
                    />
                  </div>
                  <div>
                    <Label htmlFor="basic_pay">Basic Pay (₹)</Label>
                    <Input
                      id="basic_pay"
                      type="number"
                      value={form.basic_pay}
                      onChange={(e) => handleChange('basic_pay', parseFloat(e.target.value) || 0)}
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <Label htmlFor="status">Status</Label>
                    <Select value={form.status} onValueChange={(v) => handleChange('status', v)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                        <SelectItem value="on_leave">On Leave</SelectItem>
                        <SelectItem value="probation">Probation</SelectItem>
                        <SelectItem value="suspended">Suspended</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>

              {/* Probation Card */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle>Probation Details</CardTitle>
                  <CardDescription>If employee is on probation</CardDescription>
                </CardHeader>
                <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="is_on_probation"
                      checked={form.is_on_probation}
                      onChange={(e) => handleChange('is_on_probation', e.target.checked)}
                      className="w-4 h-4"
                    />
                    <Label htmlFor="is_on_probation">On Probation</Label>
                  </div>
                  <div>
                    <Label htmlFor="probation_period_months">Period (Months)</Label>
                    <Input
                      id="probation_period_months"
                      type="number"
                      value={form.probation_period_months}
                      onChange={(e) => handleChange('probation_period_months', parseInt(e.target.value) || '')}
                      disabled={!form.is_on_probation}
                    />
                  </div>
                  <div>
                    <Label htmlFor="probation_start_date">Start Date</Label>
                    <Input
                      id="probation_start_date"
                      type="date"
                      value={form.probation_start_date}
                      onChange={(e) => handleChange('probation_start_date', e.target.value)}
                      disabled={!form.is_on_probation}
                    />
                  </div>
                  <div>
                    <Label htmlFor="probation_end_date">End Date</Label>
                    <Input
                      id="probation_end_date"
                      type="date"
                      value={form.probation_end_date}
                      onChange={(e) => handleChange('probation_end_date', e.target.value)}
                      disabled={!form.is_on_probation}
                    />
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Contact Tab */}
          <TabsContent value="contact">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Phone className="w-5 h-5 text-blue-600" />
                  Contact Information
                </CardTitle>
                <CardDescription>Phone numbers and email addresses</CardDescription>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="mobile_primary">Primary Mobile <span className="text-red-500">*</span></Label>
                  <Input
                    id="mobile_primary"
                    value={form.mobile_primary}
                    onChange={(e) => handleChange('mobile_primary', e.target.value)}
                    placeholder="10-digit mobile number"
                    required
                    data-testid="input-mobile"
                  />
                </div>
                <div>
                  <Label htmlFor="mobile_secondary">Secondary Mobile</Label>
                  <Input
                    id="mobile_secondary"
                    value={form.mobile_secondary}
                    onChange={(e) => handleChange('mobile_secondary', e.target.value)}
                    placeholder="Alternate mobile"
                  />
                </div>
                <div>
                  <Label htmlFor="phone_residence">Residence Phone</Label>
                  <Input
                    id="phone_residence"
                    value={form.phone_residence}
                    onChange={(e) => handleChange('phone_residence', e.target.value)}
                    placeholder="Landline number"
                  />
                </div>
                <div>
                  <Label htmlFor="phone_office">Office Phone</Label>
                  <Input
                    id="phone_office"
                    value={form.phone_office}
                    onChange={(e) => handleChange('phone_office', e.target.value)}
                    placeholder="Office extension"
                  />
                </div>
                <div>
                  <Label htmlFor="personal_email">Personal Email</Label>
                  <Input
                    id="personal_email"
                    type="email"
                    value={form.personal_email}
                    onChange={(e) => handleChange('personal_email', e.target.value)}
                    placeholder="personal@email.com"
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Identity Tab */}
          <TabsContent value="identity">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-blue-600" />
                  Identity Documents
                </CardTitle>
                <CardDescription>Government ID and verification details</CardDescription>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="aadhaar_number">Aadhaar Number</Label>
                  <Input
                    id="aadhaar_number"
                    value={form.aadhaar_number}
                    onChange={(e) => handleChange('aadhaar_number', e.target.value.replace(/\D/g, '').slice(0, 12))}
                    placeholder="12-digit Aadhaar"
                    maxLength={12}
                  />
                </div>
                <div>
                  <Label htmlFor="pan_number">PAN Number</Label>
                  <Input
                    id="pan_number"
                    value={form.pan_number}
                    onChange={(e) => handleChange('pan_number', e.target.value.toUpperCase())}
                    placeholder="ABCDE1234F"
                    maxLength={10}
                  />
                </div>
                <div>
                  <Label htmlFor="voter_id">Voter ID</Label>
                  <Input
                    id="voter_id"
                    value={form.voter_id}
                    onChange={(e) => handleChange('voter_id', e.target.value.toUpperCase())}
                    placeholder="Voter ID number"
                  />
                </div>
                <div>
                  <Label htmlFor="passport_number">Passport Number</Label>
                  <Input
                    id="passport_number"
                    value={form.passport_number}
                    onChange={(e) => handleChange('passport_number', e.target.value.toUpperCase())}
                    placeholder="Passport number"
                  />
                </div>
                <div>
                  <Label htmlFor="passport_expiry_date">Passport Expiry</Label>
                  <Input
                    id="passport_expiry_date"
                    type="date"
                    value={form.passport_expiry_date}
                    onChange={(e) => handleChange('passport_expiry_date', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="driving_license_number">Driving License</Label>
                  <Input
                    id="driving_license_number"
                    value={form.driving_license_number}
                    onChange={(e) => handleChange('driving_license_number', e.target.value.toUpperCase())}
                    placeholder="DL number"
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Statutory Tab */}
          <TabsContent value="statutory">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-blue-600" />
                  Statutory Details
                </CardTitle>
                <CardDescription>PF, UAN, PRAN and other statutory numbers</CardDescription>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="epf_number">EPF Number</Label>
                  <Input
                    id="epf_number"
                    value={form.epf_number}
                    onChange={(e) => handleChange('epf_number', e.target.value)}
                    placeholder="EPF account number"
                  />
                </div>
                <div>
                  <Label htmlFor="uan_number">UAN</Label>
                  <Input
                    id="uan_number"
                    value={form.uan_number}
                    onChange={(e) => handleChange('uan_number', e.target.value)}
                    placeholder="Universal Account Number"
                  />
                </div>
                <div>
                  <Label htmlFor="gpf_number">GPF Number</Label>
                  <Input
                    id="gpf_number"
                    value={form.gpf_number}
                    onChange={(e) => handleChange('gpf_number', e.target.value)}
                    placeholder="GPF account number"
                  />
                </div>
                <div>
                  <Label htmlFor="pran_number">PRAN</Label>
                  <Input
                    id="pran_number"
                    value={form.pran_number}
                    onChange={(e) => handleChange('pran_number', e.target.value)}
                    placeholder="Permanent Retirement Account Number"
                  />
                </div>
                <div>
                  <Label htmlFor="esi_number">ESI Number</Label>
                  <Input
                    id="esi_number"
                    value={form.esi_number}
                    onChange={(e) => handleChange('esi_number', e.target.value)}
                    placeholder="ESI number"
                  />
                </div>
                <div>
                  <Label htmlFor="accommodation_type">Accommodation Type</Label>
                  <Select value={form.accommodation_type} onValueChange={(v) => handleChange('accommodation_type', v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      <SelectItem value="own">Own</SelectItem>
                      <SelectItem value="rented">Rented</SelectItem>
                      <SelectItem value="company_provided">Company Provided</SelectItem>
                      <SelectItem value="government_quarters">Government Quarters</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </form>
    </div>
  );
};

export default EmployeeMasterForm;
